import Container from '@mui/material/Container'
import CssBaseline from '@mui/material/CssBaseline'
import GlobalStyles from '@mui/material/GlobalStyles'
import { ThemeProvider } from '@mui/material/styles'
import { useEffect, useMemo, useState } from 'react'
import { Outlet, useNavigate } from 'react-router-dom'
import packageJSON from '../package.json'
import StyledSnackbar from './components/AppLayout/StyledSnackbar/StyledSnackbar'
import HeaderBar from './components/HeaderBar'
import {
    API_URL,
    APPLE_CLIENT_ID,
    APPLE_REDIRECT_URL,
    ENV,
    FACEBOOK_API_VERSION,
    FACEBOOK_APP_ID,
    FORUMS_URL,
    GA_GAME_KEY,
    GA_SECRET_KEY,
    GOOGLE_CLIENT_ID,
    GOOGLE_CLIENT_ID_ANDROID,
    GOOGLE_CLIENT_ID_IOS,
    GTM_ID,
    LEARNING_URL,
    SHOP_URL,
    WEBSITE_URL,
} from './env'
import { DailyBonusService } from './features/DailyBonusService'
import DeepLinksService from './features/deepLinks/DeepLinksService'
import { EMOTES_ENABLED } from './features/gameView/emotes/EmotesModel'
import { GameViewState } from './features/gameView/GameViewModel'
import { MatchMakingConnector } from './features/matchMakingDrawer/matchMakingConnector/MatchMakingConnector'
import { MissionService } from './features/MissionService'
import { PopupService } from './features/PopupService'
import { useWebsocketGateway } from './hooks/useWebsocketGateway'
import { GlobalProvider } from './sharedComponents/src/Contexts/GlobalContext'
import { HeaderButtonsProvider } from './sharedComponents/src/Contexts/HeaderButtonsContext'
import { AreaType, LoggedInUserData, LoginState } from './sharedComponents/src/globalHeader/GlobalHeader'
import { AnalyticsLoggingServiceComponent } from './sharedComponents/src/globalHeader/services/analytics/AnalyticsLoggingServiceComponent'
import { analyticsManager } from './sharedComponents/src/globalHeader/services/analytics/AnalyticsManager'
import { GameAnalyticsServiceComponent } from './sharedComponents/src/globalHeader/services/analytics/GameAnalyticsServiceComponent'
import { GoogleAnalyticsServiceComponent } from './sharedComponents/src/globalHeader/services/analytics/GoogleAnalyticsServiceComponent'
import { boardColors } from './sharedComponents/src/globalHeader/theme/colors'
import { useStoreActions, useStoreState } from './store/hooks'
import { ColorModeContext } from './theme/colorContext'
import { getDesignTokens } from './theme/theme'

function App() {
    const wsGateway = useWebsocketGateway()
    const userData = useStoreState((state) => state.userData)
    const darkMode = useStoreState((state) => state.darkMode)
    const serverConnectionData = useStoreState((state) => state.serverConnectionData)
    const settings = useStoreState((state) => state.gameView.settings)
    const snackbarMessage = useStoreState((state) => state.snackbarMessage)
    const navigate = useNavigate()

    const setDarkMode = useStoreActions((state) => state.setDarkMode)
    const setServerConnectionData = useStoreActions((state) => state.setServerConnectionData)
    const setUserData = useStoreActions((state) => state.setUserData)
    const setSettings = useStoreActions((state) => state.gameView.setSettings)
    const setToken = useStoreActions((state) => state.setToken)
    const setRules = useStoreActions((state) => state.setRules)
    const setUserGroups = useStoreActions((state) => state.setUserGroups)
    const shutDownGSConnection = useStoreActions((state) => state.gameView.shutDownGSConnection)
    const resetGame = useStoreActions((state) => state.gameView.resetGame)
    const reset = useStoreActions((state) => state.matchMaker.reset)
    const setSnackbarMessage = useStoreActions((state) => state.setSnackbarMessage)
    const setRematchStatus = useStoreActions((state) => state.matchMaker.setRematchStatus)
    const determineIfNewUser = useStoreActions((state) => state.profileView.determineIfNewUser)

    const [googleAnalyticsLoaded, setGoogleAnalyticsLoaded] = useState<boolean>(false)
    const [gameAnalyticsLoaded, setGameAnalyticsLoaded] = useState<boolean>(false)
    const [snackBarOpen, setSnackBarOpen] = useState(false)

    const mode = darkMode ? 'dark' : 'light'

    const colorMode = {
        toggleColorMode: () => {
            setDarkMode(!darkMode)
        },
    }

    let boardColor = useMemo(() => {
        return boardColors[settings.boardStyle] || boardColors.default
    }, [settings.boardStyle])

    // user can press f9 to toggle serverConnectionData
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'F9') {
                setServerConnectionData(!serverConnectionData)
            }
        }
        window.addEventListener('keydown', handleKeyDown)
        return () => {
            window.removeEventListener('keydown', handleKeyDown)
        }
    }, [serverConnectionData])

    useEffect(() => {
        if (googleAnalyticsLoaded && gameAnalyticsLoaded) {
            analyticsManager.markAsLoaded()
        }
    }, [googleAnalyticsLoaded, gameAnalyticsLoaded])

    useEffect(() => {
        if (snackbarMessage !== undefined) {
            setSnackBarOpen(true)
        }
    }, [snackbarMessage])

    const handleSnackBarOpenClose = () => {
        setSnackBarOpen(false)
        setRematchStatus(undefined)
        setSnackbarMessage(undefined)
    }

    // creates a theme based on the mode which is used by theme provider. CssBaseLine is needed to reset the default styles on html/body
    const theme = useMemo(() => getDesignTokens(mode, boardColor), [mode, boardColor])

    const onLoginStateChange = (userData: LoggedInUserData) => {
        setUserData({
            state: userData.state as LoginState,
            userData: userData.userData,
            token: userData.token,
        })
        if (userData.userData?.groups) {
            const ruleNames = userData.userData.groups.flatMap((group) => group.rules.map((rule) => rule.name))
            setRules(ruleNames)
            const userGroups = userData.userData.groups.map((group) => group.name)
            setUserGroups(userGroups)
        }
        if (userData.token) {
            setToken(userData.token)
        }
        if (userData.state === LoginState.NOT_LOGGED_IN) {
            console.log('resetting game server, matchmaker and observe server')
            shutDownGSConnection()
            resetGame(GameViewState.PRE_GAME)
            reset()
            setUserData({ state: LoginState.NOT_LOGGED_IN, userData: null, token: '' })
            setToken('')
        }
        if (userData.userData?.game_settings) setSettings(userData.userData?.game_settings)
        if (userData.userData?.created_at) {
            determineIfNewUser({
                date: userData.userData.created_at,
                seniority: userData.userData.user_config.main_menu_seniority,
            })
        }
    }

    const onSettingsChange = (updatedSettings) => {
        if (userData.userData) {
            setSettings(updatedSettings)
            setUserData({ ...userData, userData: { ...userData.userData, game_settings: updatedSettings } })
        }
    }

    const globalProviderProps = {
        apiURL: API_URL,
        forumsURL: FORUMS_URL,
        learningURL: LEARNING_URL,
        playAreaURL: '',
        websiteURL: WEBSITE_URL,
        shopURL: SHOP_URL,
        env: ENV,
        googleClientId: GOOGLE_CLIENT_ID,
        googleClientIdAndroid: GOOGLE_CLIENT_ID_ANDROID,
        googleClientIdIos: GOOGLE_CLIENT_ID_IOS,
        facebookAppId: FACEBOOK_APP_ID,
        facebookApiVersion: FACEBOOK_API_VERSION,
        appleClientId: APPLE_CLIENT_ID,
        appleRedirectURL: APPLE_REDIRECT_URL,
        initUserData: userData.userData || null,
        area: AreaType.PLAY_AREA,
        pvpEmotes: EMOTES_ENABLED,
        onLoginStateChange: onLoginStateChange,
        onSettingsChange: onSettingsChange,
        showSnackbar: setSnackbarMessage,
        navigate,
    }

    return (
        <ColorModeContext.Provider value={colorMode}>
            <ThemeProvider theme={theme}>
                <CssBaseline enableColorScheme={true} />
                <GlobalStyles
                    styles={{
                        html: {
                            '-webkit-text-size-adjust': 'none',
                            '-moz-text-size-adjust': 'none',
                            '-ms-text-size-adjust': 'none',
                            'text-size-adjust': 'none',
                        },
                    }}
                />
                <HeaderButtonsProvider>
                    <GlobalProvider {...globalProviderProps}>
                        <Container maxWidth={false} sx={{ padding: '0rem !important' }}>
                            <DeepLinksService />
                            <HeaderBar />
                            <MatchMakingConnector />
                            <Outlet />
                            <PopupService />
                            <MissionService />
                            <DailyBonusService />
                            <AnalyticsLoggingServiceComponent env={ENV} userData={userData.userData || undefined} />
                            <GameAnalyticsServiceComponent
                                gaGameKey={GA_GAME_KEY}
                                gaSecretKey={GA_SECRET_KEY}
                                version={packageJSON.version}
                                env={ENV}
                                userData={userData.userData || undefined}
                                loaded={() => setGameAnalyticsLoaded(true)}
                            />
                            <GoogleAnalyticsServiceComponent
                                env={ENV}
                                userData={userData.userData || undefined}
                                gtmId={GTM_ID}
                                loginState={userData.state}
                                loaded={() => setGoogleAnalyticsLoaded(true)}
                            />
                            <StyledSnackbar
                                isOpen={snackBarOpen}
                                message={snackbarMessage}
                                onClose={handleSnackBarOpenClose}
                            />
                        </Container>
                    </GlobalProvider>
                </HeaderButtonsProvider>
            </ThemeProvider>
        </ColorModeContext.Provider>
    )
}

export default App
